import { useCallback, useEffect, useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import VideoCard, {
  type VideoStreamCard,
} from "../../components/common/VideoCard";
import CourtFilterBar from "../../components/common/CourtFilterBar";
import { getPublicStreamsApi } from "../../api/publicMeetingJoinApi";

type ApiRecordingStatus = "Available" | "Archived" | "Processing";

interface ApiRecordingRow {
  _id?: string;
  id?: string;
  cnr?: string;
  caseNo?: string;
  caseNumber?: string;
  title?: string;
  court?: string;
  courtType?: "High Court" | "District Court";
  status?: ApiRecordingStatus | string;
  date?: string;
  duration?: string;
  viewers?: number;
  room?: string;
  hearingDate?: string;
  highCourt?: string;
  bench?: string;
  state?: string;
  district?: string;
  courtComplex?: string;
  courtEstablishment?: string;
}

const recordingStatuses = new Set<ApiRecordingStatus>([
  "Available",
  "Archived",
  "Processing",
]);

const normalizeStatus = (status?: string): ApiRecordingStatus =>
  recordingStatuses.has(status as ApiRecordingStatus)
    ? (status as ApiRecordingStatus)
    : "Processing";

const mapRecording = (row: ApiRecordingRow): VideoStreamCard => {
  const id = row.id || row.cnr || row.caseNo || row._id || "";
  const courtType =
    row.courtType === "District Court" ? "District Court" : "High Court";

  return {
    id,
    watchCnr: row.cnr || row.caseNo || row.caseNumber || String(id),
    caseNo: row.caseNo || row.cnr || row.caseNumber || String(id),
    title: row.title || row.caseNumber || row.cnr || "Courtroom Recording",
    court:
      row.court ||
      row.highCourt ||
      (row.district ? `District Court, ${row.district}` : "Court"),
    courtType,
    status: normalizeStatus(row.status),
    date: row.date || row.hearingDate,
    duration: row.duration,
    access:
      row.status === "Archived"
        ? "Request Required"
        : row.status === "Processing"
          ? "Pending"
          : "Granted",
    viewers: Number(row.viewers) || 0,
    room: row.room,
    highCourt: row.highCourt,
    bench: row.bench,
    state: row.state,
    district: row.district,
    courtComplex: row.courtComplex,
    courtEstablishment: row.courtEstablishment,
  };
};

const Recording = () => {
  const [recordings, setRecordings] = useState<VideoStreamCard[]>([]);
  const [filtered, setFiltered] = useState<VideoStreamCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchRecordings = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const res = await getPublicStreamsApi({ listType: "recordings" });
      if (!res?.data?.status) {
        throw new Error(res?.data?.message || "Failed to load recordings.");
      }

      const mapped = ((res.data.data || []) as ApiRecordingRow[])
        .map(mapRecording)
        .filter((recording) => Boolean(recording.id));

      setRecordings(mapped);
      setFiltered(mapped);
    } catch (err: any) {
      setRecordings([]);
      setFiltered([]);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load recordings.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecordings();
  }, [fetchRecordings]);

  return (
    <div className="space-y-5 p-6">
      <CourtFilterBar
        rows={recordings}
        statusOptions={["All", "Available", "Archived", "Processing"]}
        onChange={setFiltered}
      />
      {loading && (
        <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-16">
          <Loader2 className="h-7 w-7 animate-spin text-indigo-600" />
        </div>
      )}{" "}
      {error ? (
        <div className="rounded-2xl border border-red-100 bg-white px-6 py-12 text-center shadow-sm">
          <p className="text-sm font-semibold text-slate-900">{error}</p>
          <button
            type="button"
            onClick={fetchRecordings}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
          >
            <RotateCcw size={15} />
            Retry
          </button>
        </div>
      ) : (
        <VideoCard rows={filtered} watchType="recording" />
      )}
    </div>
  );
};

export default Recording;
